import { css } from 'styled-components';
import {
  BreakpointQueryName,
  getSpacing, SpacingName, ThemedFunctor,
} from '@doveit/lego';
import { StripeDirection } from './types';

const leftImageMask = 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDM5Ni40IDI3Mi4yIj48cG9seWdvbiBwb2ludHM9IjAgMCAwIDI3Mi4yIDM0Ni40MyAyNzIuMiAzOTYuNCAwIDAgMCIvPjwvc3ZnPg==';
const rightImageMask = 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz48c3ZnIGlkPSJMaXZlbGxvXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMCAwIDM5Ni40IDI3Mi4yIj48cG9seWdvbiBwb2ludHM9IjQ5Ljk3IDAgMCAyNzIuMiAzOTYuNCAyNzIuMiAzOTYuNCAwIDQ5Ljk3IDAiIHN0eWxlPSJmaWxsOiMxZDFkMWI7Ii8+PC9zdmc+';

export const base: ThemedFunctor = ({
  theme,
  $direction,
}) => {
  const colors = $direction === StripeDirection.LEFT
    ? [theme.palettes.neutrals.lightest, theme.palettes.brand.primaryLight]
    : [theme.palettes.brand.primaryLight, theme.palettes.neutrals.lightest];

  return css`
    ${getSpacing.margin(theme, $direction === StripeDirection.LEFT ? [SpacingName.SMALL, 0, 0] : [0, 0, SpacingName.SMALL])}
    background-color: ${theme.palettes.brand.primaryLight};

    ${theme.breakpoints.up(BreakpointQueryName.LG)} {
      background: linear-gradient(90deg, ${colors[0]} 50%, ${colors[1]} 50%);
    }
  `;
};

export const content: ThemedFunctor = ({
  theme,
}) => css`
  background: ${theme.palettes.brand.primaryLight};
  padding: 1px 0; /** Hack for negative margin issue */
`;

export const image: ThemedFunctor = ({
  theme,
  $direction,
}) => {
  const imageTranslationValues = {
    [StripeDirection.LEFT]: {
      mobile: ['-SMALL', '-SMALL'],
      desktop: ['-MEDIUM', '-SMALL'],
    },
    [StripeDirection.RIGHT]: {
      mobile: [0, 'SMALL'],
      desktop: [0, '-SMALL'],
    },
  };

  return css`
    width: calc(100% + ${theme.spaceUnits.mobile.SMALL});
    transform: translateX(${theme.spaceUnits.mobile[imageTranslationValues[$direction].mobile[0]]}) translateY(${theme.spaceUnits.mobile[imageTranslationValues[$direction].mobile[1]]});

    ${$direction === StripeDirection.RIGHT && `
      display: flex;
      justify-content: flex-end;
    `}

    ${theme.breakpoints.up(BreakpointQueryName.LG)} {
      width: calc(100% + ${theme.spaceUnits.desktop.MEDIUM});
      transform: translateX(${theme.spaceUnits.desktop[imageTranslationValues[$direction].desktop[0]]}) translateY(${theme.spaceUnits.desktop[imageTranslationValues[$direction].desktop[1]]});
    }
  `;
};

export const imageWrapper: ThemedFunctor = ({
  theme,
  $direction,
}) => css`
  max-width: 25rem;
  flex-grow: 1;

  img {
    width: 100%;
    line-height: 0;
    display: inline-block;
    mask-image: url(${$direction === StripeDirection.LEFT ? leftImageMask : rightImageMask});
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
  }

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    max-width: 100%;
  }
`;

export const grid = (direction: StripeDirection) => ({ theme }) => css`
  flex-direction: ${direction === StripeDirection.RIGHT ? 'column-reverse' : 'column'};

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    flex-direction: ${direction === StripeDirection.RIGHT ? 'row-reverse' : 'row'};
  }
`;

export const info: ThemedFunctor = ({
  theme,
  $direction,
}) => css`
  ${$direction === StripeDirection.LEFT ? `
    padding-bottom: ${theme.spaceUnits.mobile.BIGGER};
    margin-top: ${theme.spaceUnits.mobile['-MEDIUM']};
  ` : `
    padding-top: ${theme.spaceUnits.mobile.BIGGER};
    margin-bottom: ${theme.spaceUnits.mobile['-MEDIUM']};
  `}

  ${theme.breakpoints.up(BreakpointQueryName.LG)} {
    padding: ${theme.spaceUnits.desktop.BIGGER} 0;
    margin: 0;
  }
`;
